import currency from 'currency.js';
import moment from 'moment-timezone';
import { max } from '@popperjs/core/lib/utils/math';
import { HITOS_PAGO_TIPOCANTIDAD } from '@/constants/viajeConstants';
import { ESTADOS_RESERVA } from '@/constants/reservaConstants';
import { calculaPrecioTotal, calculaPrecioTotalSinTasasInternas } from '@/helpers/viajesGrupos';

export function calcularTotalPagado(reserva) {
  return reserva?.pagos?.reduce((accum, pago) => accum.add(pago.cantidad), currency(0)) || currency(0);
}

export function calcularTasasInternasPagadas(reserva) {
  return reserva?.pagos?.reduce((accum, pago) => accum.add(pago.fee), currency(0)) || currency(0);
}

export function calculaCantidadHito(hitoPago, precioTotalSinTasasInternas) {
  if (HITOS_PAGO_TIPOCANTIDAD.FIJO === hitoPago.tipoCantidad) {
    return currency(hitoPago.cantidad);
  } else if (HITOS_PAGO_TIPOCANTIDAD.PORCENTAJE === hitoPago.tipoCantidad) {
    return currency(hitoPago.cantidad).divide(100).multiply(precioTotalSinTasasInternas);
  } else {
    return currency(0);
  }
}

export function acumulaHitosPasadosEnActual(hitosPago, reserva, viaje, mergePastWithNext = true) {
  const fechaActual = moment();
  let cantidadAcc = currency(0);
  let sumPastHitos = currency(0);
  let hitoAcumulado = false;
  let pastFecha = null;
  let precioTotalSinTasasInternas = calculaPrecioTotalSinTasasInternas(reserva, false);
  let precioTotalSinTasasNiComplementos = precioTotalSinTasasInternas.subtract(reserva.precioComplementos);
  const result = [];

  hitosPago.forEach((hito, idx) => {
    const fechaLimite = moment.tz(hito.fecha, viaje.timezone);
    let cantidad = calculaCantidadHito(hito, precioTotalSinTasasNiComplementos);
    if (idx === 0) {
      cantidad = cantidad.add(reserva.precioComplementos);
    }
    cantidadAcc = cantidadAcc.add(cantidad);

    if (fechaActual > fechaLimite) {
      hitoAcumulado = true;
      sumPastHitos = sumPastHitos.add(cantidad);
      pastFecha = hito.fecha;
      if (idx < hitosPago.length - 1) {
        return;
      }
    }

    if (hitoAcumulado) {
      hitoAcumulado = false;
      if (mergePastWithNext) {
        let cantidadMerged = cantidadAcc;
        if (HITOS_PAGO_TIPOCANTIDAD.RESTANTE === hito.tipoCantidad) {
          cantidadMerged = precioTotalSinTasasInternas;
        }
        result.push({
          cantidad: cantidadMerged,
          tipoCantidad: hito.tipoCantidad,
          fecha: hito.fecha,
        });
        return;
      }
      result.push({
        cantidad: sumPastHitos,
        tipoCantidad: HITOS_PAGO_TIPOCANTIDAD.FIJO,
        fecha: pastFecha,
      });
      if (fechaActual > fechaLimite) {
        return;
      }
    }

    if (HITOS_PAGO_TIPOCANTIDAD.RESTANTE === hito.tipoCantidad) {
      cantidad = precioTotalSinTasasInternas.subtract(cantidadAcc);
    }
    result.push({
      cantidad,
      tipoCantidad: hito.tipoCantidad,
      fecha: hito.fecha,
    });
  });

  return result;
}

function applyCantidadesPendientes(hitosAjustados, hitosPagoFinales, ctx) {
  const {
    pagadoRestante: pagadoRestanteInicial,
    pagadoTasasInternasRestante: pagadoTasasInternasRestanteInicial,
    precioTotalSinTasasInternas,
    totalTasasInternas,
    repercutirFee,
  } = ctx;
  let pagadoRestante = pagadoRestanteInicial;
  let pagadoTasasInternasRestante = pagadoTasasInternasRestanteInicial;
  let totalTasasAcc = currency(0);

  hitosAjustados.forEach((hitoAjustado, idx) => {
    hitoAjustado.pagadoRestantePrevio = pagadoRestante;
    let cantidadHitoPendiente = currency(max(0, hitoAjustado.cantidad.subtract(pagadoRestante).value));
    pagadoRestante = currency(max(0, pagadoRestante.subtract(hitoAjustado.cantidad).value));
    if (repercutirFee) {
      let tasasNetasHito;
      if (HITOS_PAGO_TIPOCANTIDAD.RESTANTE !== hitoAjustado.tipoCantidad) {
        const ratioTasasInternas = hitoAjustado.cantidad.value / precioTotalSinTasasInternas.value;
        const tasasBrutasHito = totalTasasInternas.multiply(ratioTasasInternas);
        tasasNetasHito = max(0, tasasBrutasHito.subtract(pagadoTasasInternasRestante).value);
        pagadoTasasInternasRestante = currency(max(0, pagadoTasasInternasRestante.subtract(tasasBrutasHito).value));
        totalTasasAcc = totalTasasAcc.add(tasasBrutasHito);
      } else {
        tasasNetasHito = totalTasasInternas.subtract(totalTasasAcc).value;
      }
      cantidadHitoPendiente = cantidadHitoPendiente.add(max(0, tasasNetasHito));
    }
    hitoAjustado.cantidadPendiente = currency(max(0, cantidadHitoPendiente.value));
    if (hitosPagoFinales[idx] == null) {
      hitosPagoFinales[idx] = hitoAjustado;
    } else {
      hitosPagoFinales[idx].cantidadPendiente = hitosPagoFinales[idx].cantidadPendiente.add(
        hitoAjustado.cantidadPendiente
      );
    }
  });
}

export function calculaHitosPago(hitosPago, reservas, viaje) {
  hitosPago = hitosPago ?? [];
  const fechaActual = moment();
  const repercutirFee = viaje?.repercutirFee;
  const hitosPagoFinales = [];
  const hitosPagoFinalesPast = [];
  let totalPagadoGrupo = currency(0);
  let totalPagadoRealGrupo = currency(0);
  reservas.forEach(reserva => {
    const precioTotalSinTasasInternas = calculaPrecioTotalSinTasasInternas(reserva, false);
    const precioTotal = calculaPrecioTotal(reserva, false);
    let totalPagado = calcularTotalPagado(reserva);
    totalPagadoRealGrupo = totalPagadoRealGrupo.add(totalPagado);
    if (reserva.descuento) {
      totalPagado = totalPagado.add(reserva.descuento);
    }
    const totalTasasInternas = precioTotal.subtract(precioTotalSinTasasInternas);
    const totalTasasInternasPagadas = calcularTasasInternasPagadas(reserva);
    totalPagadoGrupo = totalPagadoGrupo.add(totalPagado);
    let pagadoRestante = totalPagado;
    if (repercutirFee) {
      pagadoRestante = pagadoRestante.subtract(totalTasasInternasPagadas);
    }

    const ctx = {
      pagadoRestante,
      pagadoTasasInternasRestante: totalTasasInternasPagadas,
      precioTotalSinTasasInternas,
      totalTasasInternas,
      repercutirFee,
    };

    applyCantidadesPendientes(acumulaHitosPasadosEnActual(hitosPago, reserva, viaje, true), hitosPagoFinales, ctx);
    applyCantidadesPendientes(acumulaHitosPasadosEnActual(hitosPago, reserva, viaje, false), hitosPagoFinalesPast, {
      ...ctx,
    });
  });

  const hitosPagoPagados = [];
  const hitosPagoPendientes = [];
  hitosPagoFinales.forEach(hito => {
    if (
      hito.cantidadPendiente.value === 0 &&
      totalPagadoGrupo.value > 0 &&
      hito.pagadoRestantePrevio.value > 0 &&
      totalPagadoRealGrupo.value > 0
    ) {
      hitosPagoPagados.push(hito);
    } else if (hito.tipoCantidad !== HITOS_PAGO_TIPOCANTIDAD.RESTANTE || hito.cantidad.value > 0) {
      hitosPagoPendientes.push(hito);
    }
  });

  const pasados = [];
  hitosPagoFinalesPast.forEach(hito => {
    const isPagado =
      hito.cantidadPendiente.value === 0 &&
      totalPagadoGrupo.value > 0 &&
      hito.pagadoRestantePrevio.value > 0 &&
      totalPagadoRealGrupo.value > 0;
    if (!isPagado && (hito.tipoCantidad !== HITOS_PAGO_TIPOCANTIDAD.RESTANTE || hito.cantidad.value > 0)) {
      pasados.push(hito);
    }
  });

  let fechaVencimientoHitoActual = null;
  if (hitosPagoPagados.length > 0) {
    const lastHitoPagoPagado = hitosPagoPagados[hitosPagoPagados.length - 1];
    if (moment.tz(lastHitoPagoPagado.fecha, viaje.timezone) >= fechaActual) {
      fechaVencimientoHitoActual = lastHitoPagoPagado.fecha;
    }
  }

  return {
    pendientes: hitosPagoPendientes,
    pasados,
    pagados: hitosPagoPagados,
    fechaVencimientoHitoActual,
    hitosPagoFinales,
    totalPagadoGrupo,
    totalPagadoRealGrupo,
  };
}

export function ordenarHitosPago(hitosPago, viaje) {
  return [...hitosPago].sort((a, b) => {
    const momentA = moment.tz(a.fecha, viaje.timezone);
    const momentB = moment.tz(b.fecha, viaje.timezone);
    return momentA.diff(momentB) || a.hora.localeCompare(b.hora);
  });
}

export function syncHitosWithPagos(hitosPago, reservas, viaje) {
  const hitosOrdenados = ordenarHitosPago(hitosPago, viaje);
  const activas = reservas.filter(reserva => reserva.estado !== ESTADOS_RESERVA.CANCELADA);

  const hitosConPagos = hitosOrdenados.map((hito, idx) => ({
    numero: idx + 1,
    fechaVencimiento: hito.fecha,
    fechasPago: [],
    solicitudCobroIds: [],
    personasPagadas: [],
    personasPendientes: [],
  }));

  activas.forEach(reserva => {
    let totalPagado = calcularTotalPagado(reserva);
    if (reserva.descuento) {
      totalPagado = totalPagado.add(reserva.descuento);
    }

    const hitosPagoAjustados = acumulaHitosPasadosEnActual(hitosOrdenados, reserva, viaje);
    let pagadoRestante = totalPagado;
    const pagos = (reserva.pagos ?? []).map(p => ({
      restante: p.cantidad,
      fecha: p.fecha,
      id: p.id,
    }));

    hitosPagoAjustados.forEach((hitoAjustado, idx) => {
      const importeSync = hitoAjustado.cantidad.value;
      const cubiertoSync = Math.min(importeSync, pagadoRestante.value);
      let porCubrir = cubiertoSync;
      let solicitudCobroId = null;
      let fechaPago = null;

      for (const pago of pagos) {
        if (porCubrir <= 0) break;
        const uso = Math.min(porCubrir, pago.restante);
        if (uso > 0) {
          if (pago.fecha) {
            hitosConPagos[idx].fechasPago.push(pago.fecha);
            fechaPago = pago.fecha;
          }
          if (pago.id) {
            hitosConPagos[idx].solicitudCobroIds.push(pago.id);
            solicitudCobroId = pago.id;
          }
          pago.restante -= uso;
          porCubrir -= uso;
        }
      }

      const pagadoCompleto = cubiertoSync >= importeSync - 0.01;

      if (pagadoCompleto && reserva.nombreUsuario) {
        hitosConPagos[idx].personasPagadas.push({
          nombre: reserva.nombreUsuario,
          solicitudCobroId,
          fecha: fechaPago,
          importe: importeSync,
        });
      } else if (cubiertoSync < importeSync - 0.01 && reserva.nombreUsuario) {
        hitosConPagos[idx].personasPendientes.push(reserva.nombreUsuario);
      }

      pagadoRestante = currency(max(0, pagadoRestante.subtract(hitoAjustado.cantidad).value));
    });
  });

  return { hitosConPagos };
}

export function calcularPagoRestante(precioTotal, totalPagado) {
  return currency(precioTotal).subtract(totalPagado);
}
